import { exceedsPrinted, WB_DECIMALS } from '@/utils/number'

/**
 * Ordem entre os pesos máximos de um perfil de P&B.
 *
 * As regras moram aqui, em módulo puro, para que o domínio e o formulário decidam pelo mesmo
 * critério — divergir aqui é o que produzia perfil reprovado na tela e aprovado na gravação.
 *
 * Todas são **inclusivas**: um manual que publica rampa igual à decolagem (aeronave sem tolerância
 * de táxi declarada) é caso legítimo, não violação.
 *
 * Todas são acusadas no formulário, mas só as quatro contra o peso básico vazio reprovam o
 * perfil: abaixo do peso vazio não há carregamento que passe, e essa checagem antecede a PLA-310.
 * As da corrente — `CROSS_MAXIMUM_WEIGHT_RULES` — avisam e deixam seguir, para que os perfis já
 * gravados com elas continuem escolhíveis num P&B novo (PLA-323).
 *
 * Comparar os números do perfil entre si é seguro porque os quatro máximos e o peso vazio vivem na
 * mesma `weightUnit`, e a troca de unidade converte todos juntos.
 */

/** As regras que comparam um máximo com outro máximo — os seis pares da corrente. */
export type CrossMaximumWeightRuleId =
  | 'zeroFuelBelowLanding'
  | 'zeroFuelBelowTakeoff'
  | 'zeroFuelBelowRamp'
  | 'landingBelowTakeoff'
  | 'landingBelowRamp'
  | 'takeoffBelowRamp'

/** As regras que comparam um máximo com o peso básico vazio — as únicas que reprovam o perfil. */
export type BlockingMaximumWeightRuleId =
  'takeoffAboveEmpty' | 'zeroFuelAboveEmpty' | 'rampAboveEmpty' | 'landingAboveEmpty'

export type MaximumWeightRuleId = BlockingMaximumWeightRuleId | CrossMaximumWeightRuleId

export interface MaximumWeightsSnapshot {
  basicEmptyWeight?: number
  maxTakeoffWeight?: number
  maxZeroFuelWeight?: number
  maxRampWeight?: number
  maxLandingWeight?: number
}

/** Os quatro campos de peso máximo do snapshot. */
type MaximumWeightKey = 'maxZeroFuelWeight' | 'maxLandingWeight' | 'maxTakeoffWeight' | 'maxRampWeight'

/**
 * Peso ausente e peso zero contam igual: zero não é máximo que uma aeronave declare, e é o que o
 * formulário entrega quando o piloto apaga o campo. Regra sem os dois lados preenchidos não é
 * violada — é indecidível, e a ausência do que é obrigatório é cobrada em outro lugar.
 */
const isFilled = (weight?: number): boolean => typeof weight === 'number' && Number.isFinite(weight) && weight > 0

/**
 * Os quatro máximos na ordem que a aeronave guarda, do limite mais leve para o mais pesado.
 *
 * Cada um é o de decolagem movido por uma quantidade de combustível: zero combustível é a decolagem
 * sem nenhum, o de pouso é a decolagem menos o que o voo queima, o de rampa é a decolagem mais o que
 * o táxi queima. Por isso a regra inteira é uma corrente só.
 */
const MAXIMUM_WEIGHT_CHAIN: MaximumWeightKey[] = [
  'maxZeroFuelWeight',
  'maxLandingWeight',
  'maxTakeoffWeight',
  'maxRampWeight'
]

/**
 * A regra de cada par da corrente, indexada pelo peso que tem de ficar abaixo e pelo que o segura.
 *
 * São os seis pares, e não os três vizinhos, porque um peso em branco no meio não desfaz a ordem
 * entre os que ficaram: sem o de pouso, quem segura o de zero combustível é o de decolagem.
 */
const CHAIN_PAIR_RULES: Record<MaximumWeightKey, Partial<Record<MaximumWeightKey, CrossMaximumWeightRuleId>>> = {
  maxZeroFuelWeight: {
    maxLandingWeight: 'zeroFuelBelowLanding',
    maxTakeoffWeight: 'zeroFuelBelowTakeoff',
    maxRampWeight: 'zeroFuelBelowRamp'
  },
  maxLandingWeight: {
    maxTakeoffWeight: 'landingBelowTakeoff',
    maxRampWeight: 'landingBelowRamp'
  },
  maxTakeoffWeight: {
    maxRampWeight: 'takeoffBelowRamp'
  },
  maxRampWeight: {}
}

/**
 * As da corrente não reprovam o perfil: quem as viola é avisado no passo dos pesos máximos, uma vez,
 * e segue.
 */
export const CROSS_MAXIMUM_WEIGHT_RULES: CrossMaximumWeightRuleId[] = [
  'zeroFuelBelowLanding',
  'zeroFuelBelowTakeoff',
  'zeroFuelBelowRamp',
  'landingBelowTakeoff',
  'landingBelowRamp',
  'takeoffBelowRamp'
]

export const isCrossMaximumWeightRule = (rule: MaximumWeightRuleId): rule is CrossMaximumWeightRuleId =>
  (CROSS_MAXIMUM_WEIGHT_RULES as MaximumWeightRuleId[]).includes(rule)

/**
 * Quais regras o perfil viola: primeiro as que o reprovam, depois as da corrente.
 *
 * PLA-339: a corrente é percorrida comparando cada peso preenchido com o **próximo preenchido**, e
 * não os três contra a decolagem. Como a ordem é transitiva, no máximo três comparações cobrem os
 * seis pares — inclusive zero combustível contra pouso, que nenhuma comparação terminada na
 * decolagem alcançava —, e a mensagem nomeia o limite que de fato segura: com o de pouso preenchido,
 * quem contém o de zero combustível é o pouso, não a decolagem acima dele.
 *
 * A comparação é sobre o valor **impresso** (`exceedsPrinted`, PLA-334/PLA-336): dois pesos que a
 * tela mostra iguais nunca viram violação por um resíduo de ponto flutuante que o piloto não vê.
 */
export function getMaximumWeightRuleViolations(snapshot: MaximumWeightsSnapshot): MaximumWeightRuleId[] {
  const { basicEmptyWeight, maxTakeoffWeight, maxZeroFuelWeight, maxRampWeight, maxLandingWeight } = snapshot

  const violations: MaximumWeightRuleId[] = []

  if (isFilled(basicEmptyWeight)) {
    if (isFilled(maxTakeoffWeight) && exceedsPrinted(basicEmptyWeight, maxTakeoffWeight, WB_DECIMALS.weight)) {
      violations.push('takeoffAboveEmpty')
    }
    if (isFilled(maxZeroFuelWeight) && exceedsPrinted(basicEmptyWeight, maxZeroFuelWeight, WB_DECIMALS.weight)) {
      violations.push('zeroFuelAboveEmpty')
    }
    if (isFilled(maxRampWeight) && exceedsPrinted(basicEmptyWeight, maxRampWeight, WB_DECIMALS.weight)) {
      violations.push('rampAboveEmpty')
    }
    if (isFilled(maxLandingWeight) && exceedsPrinted(basicEmptyWeight, maxLandingWeight, WB_DECIMALS.weight)) {
      violations.push('landingAboveEmpty')
    }
  }

  const filled = MAXIMUM_WEIGHT_CHAIN.filter((key) => isFilled(snapshot[key]))

  filled.forEach((key, index) => {
    const bound = filled[index + 1]
    if (!bound) return
    if (!exceedsPrinted(snapshot[key], snapshot[bound], WB_DECIMALS.weight)) return

    violations.push(CHAIN_PAIR_RULES[key][bound])
  })

  return violations
}
